import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { HiChevronLeft } from 'react-icons/hi';
import { CgSpinner } from 'react-icons/cg';

import { Text, Menu } from 'modules/common';

const ALIGN_OPTS = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

function GenericPagination({
  value,
  align,
  distance,
  onChange,
  isLoading,
  hasLastPage,
  hasFirstPage,
  dotsClassName,
  arrowClassName,
  numberClassName,
  activeClassName,
  numberWidthClassName,
  numberHeightClassName,
  isMobile,
}) {
  const [currentPage, setCurrentPage] = React.useState();
  const [mapTotalPages, setMapTotalPages] = React.useState([]);
  const [isDotsListShow, setIsDotsListShow] = React.useState(false);
  const [isDotsList2Show, setIsDotsList2Show] = React.useState(false);
  const [mapTotalPagesFormat, setMapTotalPagesFormat] = React.useState([]);

  // const currentTotal = React.useMemo(() => {
  //   let v = value?.current_page * value?.per_page;

  //   if (value?.total_pages === value?.current_page) {
  //     v = v - value?.per_page + value?.count;
  //   }

  //   return v;
  // }, [value]);

  const $numberClassName = cn(
    'flex items-center justify-center hover:bg-dark',
    numberClassName
  );
  const $arrowClassName = cn(
    'flex items-center justify-center hover:bg-dark !w-[24px]',
    arrowClassName
  );
  const $dotsClassName = cn('flex items-center justify-center', dotsClassName);

  const dotsList = () => {
    const dispatch = () => {
      const a = mapTotalPagesFormat?.[0]?.value - 1;

      if (a < 0) return 0;

      return a;
    };

    return [...new Array(dispatch() - 1)]
      ?.map((item, key) => {
        const obj = (mapTotalPagesFormat?.[0] || {})?.value - (key + 1);

        return {
          label: obj,
          value: obj,
        };
      })
      ?.reverse();
  };

  const dotsList2 = () => {
    const dispatch = () => {
      const a = mapTotalPagesFormat?.at(-1)?.value + 1;

      if (a < 0) return 0;

      return a;
    };

    return [...new Array(value?.total - dispatch())]?.map((item, key) => {
      const obj = (mapTotalPagesFormat?.at(-1) || {})?.value + key + 1;

      return {
        label: obj,
        value: obj,
      };
    });
  };

  const handleClick = ($value) => () => {
    if (isLoading) return;

    setIsDotsListShow(false);
    setIsDotsList2Show(false);

    setCurrentPage($value);
  };

  // eslint-disable-next-line no-unused-vars
  const handleArrowClick = (type) => () => {
    if (isLoading) return;

    setIsDotsListShow(false);
    setIsDotsList2Show(false);

    if (type === 'next') {
      if (currentPage === value?.total) return;

      setCurrentPage(currentPage + 1);
      // onChange(currentPage + 1);
      return;
    }

    if (currentPage === 1) return;
    setCurrentPage(currentPage - 1);
    // onChange(currentPage - 1);
  };

  // eslint-disable-next-line no-unused-vars
  const handleArrowClickV2 = (type) => () => {
    if (isLoading) return;

    setIsDotsListShow(false);
    setIsDotsList2Show(false);

    if (type === 'next') {
      const newPage = value?.current_page + 1;
      if (newPage > value?.total_pages) return;
      onChange(newPage);

      return;
    }

    const newPage = value?.current_page - 1;
    if (newPage <= 0) return;
    onChange(newPage);
  };

  React.useEffect(() => {
    let arr = [];
    const mapDistance = [...new Array(distance)];

    // pages lower than current page
    mapDistance?.forEach((item, key) => {
      const subtract = currentPage - (distance - key);

      if (subtract <= 0) return;

      arr = [...arr, subtract];
    });

    // current page - always center
    arr = [...arr, currentPage];

    // pages greater than current page
    mapDistance?.forEach((item, key) => {
      const add = currentPage + key + 1;

      if (add > value?.total) return;

      arr = [...arr, add];
    });

    const totalNumbers = distance * 2 + 1;

    if (arr?.length < totalNumbers) {
      const ctr = totalNumbers > value?.total ? value?.total : totalNumbers;
      const newMapDistance = [...new Array(ctr - arr?.length)];

      newMapDistance?.forEach(() => {
        if (arr[0] === 1) {
          arr = [...arr, arr[arr?.length - 1] + 1];
        } else {
          arr = [arr[0] - 1, ...arr];
        }
      });
    }

    setMapTotalPages([...arr]);
    setMapTotalPagesFormat(
      arr?.map((item) => ({
        value: item,
        className:
          currentPage === (item || null) ? activeClassName : $numberClassName,
      }))
    );
  }, [value?.total, currentPage, distance, activeClassName, $numberClassName]);

  React.useEffect(() => {
    let v;

    if (value?.current_page === 1) {
      v = 1;
      setCurrentPage(v);
      return;
    }

    v = value?.current_page * value?.per_page - value?.per_page + 1;
    setCurrentPage(v);
  }, [value?.current_page, value?.per_page]);

  return (
    <div className="flex w-full items-center">
      <Text type="caption" className="flex-1" color="muted">
        {/* {currentPage} - {currentTotal} of {value?.total} items */}
        {currentPage} of {value?.total} items
      </Text>

      <div
        className={cn(
          'flex',
          numberHeightClassName,
          ALIGN_OPTS?.[align] || 'justify-end'
        )}
      >
        {isLoading && (
          <div
            className={cn('flex self-stretch mr-ds-12', {
              'md:hidden': !isMobile,
            })}
          >
            <CgSpinner className="h-5 w-5 animate-spin m-auto" />
          </div>
        )}

        <div className="flex customize-theme-pagination text-caption">
          <button
            type="button"
            className={cn(
              'pagination-block pagination-arrow-l mr-2',
              numberWidthClassName,
              $arrowClassName,
              {
                'opacity-50 cursor-not-allowed': currentPage === 1,
              }
            )}
            onClick={handleArrowClick('previous')}
            disabled={currentPage === 1 || isLoading}
          >
            <HiChevronLeft className="h-5 w-5" />
          </button>

          <div className="flex">
            {/* dots and first page */}
            {hasFirstPage && (
              <>
                {!mapTotalPages.includes(1) && mapTotalPages[0] !== 1 && (
                  <button
                    type="button"
                    onClick={handleClick(1)}
                    className={cn(
                      'pagination-block',
                      numberWidthClassName,
                      $numberClassName
                    )}
                    disabled={isLoading}
                  >
                    1
                  </button>
                )}
                {!mapTotalPages.includes(1) && mapTotalPages[0] !== 1 + 1 && (
                  <button
                    type="button"
                    className={cn(
                      'pagination-block bg-light hover:bg-dark relative',
                      numberWidthClassName,
                      $dotsClassName
                    )}
                  >
                    <div
                      className="h-full w-full flex"
                      role="presentation"
                      onClick={() => {
                        setIsDotsListShow(!isDotsListShow);
                        setIsDotsList2Show(false);
                      }}
                    >
                      <div className="m-auto">...</div>
                    </div>

                    {isDotsListShow && (
                      <Menu
                        options={dotsList()}
                        onChange={(item) => handleClick(item?.value)()}
                        className="!top-8"
                        width="47px"
                        maxHeight="160px"
                        menuItemProps={{
                          textProps: {
                            className: 'text-center',
                          },
                        }}
                      />
                    )}
                  </button>
                )}
              </>
            )}

            {/* page numbers */}
            {mapTotalPagesFormat?.map((item, key) => (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={key}
                type="button"
                onClick={handleClick(item?.value)}
                className={cn(
                  'pagination-block pagination-number font-semibold',
                  numberWidthClassName,
                  item?.className
                )}
                disabled={isLoading}
              >
                {item?.value || ''}
              </button>
            ))}

            {/* dots and last page */}
            {hasLastPage && (
              <>
                {!mapTotalPages.includes(value?.total) &&
                  mapTotalPages[mapTotalPages?.length - 1] !==
                    value?.total - 1 && (
                    <button
                      type="button"
                      className={cn(
                        'pagination-block bg-light hover:bg-dark relative',
                        numberWidthClassName,
                        $dotsClassName
                      )}
                    >
                      <div
                        className="h-full w-full flex"
                        role="presentation"
                        onClick={() => {
                          setIsDotsList2Show(!isDotsList2Show);
                          setIsDotsListShow(false);
                        }}
                      >
                        <div className="m-auto">...</div>
                      </div>

                      {isDotsList2Show && (
                        <Menu
                          options={dotsList2()}
                          onChange={(item) => handleClick(item?.value)()}
                          className="!top-8"
                          width="47px"
                          maxHeight="160px"
                          menuItemProps={{
                            textProps: {
                              className: 'text-center',
                            },
                          }}
                        />
                      )}
                    </button>
                  )}
                {!mapTotalPages.includes(value?.total) &&
                  mapTotalPages[mapTotalPages?.length - 1] !== value?.total && (
                    <button
                      type="button"
                      onClick={handleClick(value?.total)}
                      className={cn(
                        'pagination-block',
                        numberWidthClassName,
                        $numberClassName
                      )}
                      disabled={isLoading}
                    >
                      {value?.total}
                    </button>
                  )}
              </>
            )}
          </div>

          <button
            type="button"
            className={cn(
              'pagination-block pagination-arrow-r ml-2',
              numberWidthClassName,
              $arrowClassName,
              {
                'opacity-50 cursor-not-allowed': currentPage === value?.total,
              }
            )}
            onClick={handleArrowClick('next')}
            disabled={currentPage === value?.total || isLoading}
          >
            <HiChevronLeft className="h-5 w-5 rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
}

GenericPagination.defaultProps = {
  distance: 2,
  align: 'right',
  isLoading: false,
  hasLastPage: false,
  onChange: () => {},
  hasFirstPage: false,
  numberWidthClassName: 'w-[32px]',
  numberHeightClassName: 'h-[32px]',
  dotsClassName: '',
  numberClassName: '',
  arrowClassName: '',
  activeClassName:
    ' flex items-center justify-center !bg-brand-secondary-base text-primitive-white',
  isMobile: false,
  value: {},
};

GenericPagination.propTypes = {
  align: PropTypes.string,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  distance: PropTypes.number,
  hasLastPage: PropTypes.bool,
  hasFirstPage: PropTypes.bool,
  isMobile: PropTypes.bool,
  value: PropTypes.instanceOf(Object),
  dotsClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  activeClassName: PropTypes.string,
  numberClassName: PropTypes.string,
  numberWidthClassName: PropTypes.string,
  numberHeightClassName: PropTypes.string,
};

export default GenericPagination;
